// The sidebar's order as the person arranged it: every session's id, first to last, kept in this browser
// (localStorage "cmx.order") as pins are (ui/pins.ts: the hub has no place for either yet). Empty until a row is
// moved: until then, and for a session started since, the order they were started. Pinned ones still come first.

import { useSyncExternalStore } from 'react';
import { store } from '../store';
import { arranged, moved } from './sessionOrder';

const KEY = 'cmx.order';

function read(): number[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((n): n is number => Number.isInteger(n)) : [];
  } catch {
    return [];
  }
}

let order: number[] = read();
const listeners = new Set<() => void>();

function set(next: number[]) {
  if (next.length === order.length && next.every((id, i) => id === order[i])) return;
  order = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(order));
  } catch {
    // private window or blocked storage: the order lasts for this visit
  }
  listeners.forEach((l) => l());
  // lists derived from the sessions (⌘1–9, the palette, the empty pane's picker) take the new order
  store.set({ sessions: [...store.get().sessions] });
}

export const getOrder = (): readonly number[] => order;

/** Moves `ids` (a row's session, or a group's lead and what it holds) together to before `before`'s row (null: last). */
export const moveRows = (sessions: { id: number }[], ids: number[], before: number | null) =>
  set(
    moved(
      arranged(sessions, order).map((s) => s.id),
      ids,
      before,
    ),
  );

/** Forgets sessions that are gone. Not on an empty list (between connections): that would forget everything. */
export const pruneOrder = (live: number[]) => {
  if (live.length) set(order.filter((id) => live.includes(id)));
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return;
    order = read();
    l();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
};
export const useOrder = () => useSyncExternalStore(subscribe, getOrder);
